import { Divider, Dropdown, Menu, Slider, Tooltip } from 'antd';
import dynamic from 'next/dynamic';
import { memo, RefObject, useCallback, useEffect, useMemo } from 'react';
import Hotkeys from 'react-hot-keys';
import styled from 'styled-components';

import { Button } from '../../_atoms/Button';
const DesignerSlideList = dynamic(() => import('../../_molecules/DesignerSlideList'));

import { useFlag } from '@unleash/proxy-client-react';
import { useTranslation } from 'react-i18next';
import InfiniteViewer from 'react-infinite-viewer';
import { useIntercom } from 'react-use-intercom';

import { ArrowLeftSvg, QuestionMarkSvg } from '../../../assets/icons';
import AnswerSvg from '../../../assets/icons/AnswerSvg';
import AttachmentSvg from '../../../assets/icons/AttachmentSvg';
import theme from '../../../assets/theme';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import {
  attachmentsSelector,
  selectedSlideSelector,
  TemplateStateActions,
  useDesignerDataContext,
} from '../../../hooks/useDesignerLogic';
import {
  DesignerUIStateActions,
  useDesignerUIContext,
} from '../../../hooks/useDesignerUIState';
import { useMetrics } from '../../../hooks/useMetrics';
import { useIsIntercomOpen } from '../../../providers/CustomIntercomStateProvider';
import { zIndexes } from '../../../utils/zIndexes';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { Slide } from '../../SlideComponents/slide.types';
import {
  percentageToZoomValue,
  zoomValueToPercentage,
} from '../../SlideComponents/slideUtil';

const FloatingSlideOverviewXY = ({
  designerSlideEditorParentRef,
  infiniteViewerRef,
  zoom,
  editingAreaRef,
}: {
  designerSlideEditorParentRef: RefObject<HTMLDivElement>;
  infiniteViewerRef: RefObject<InfiniteViewer>;
  zoom: number;
  editingAreaRef: RefObject<HTMLDivElement>;
}) => {
  const tabState = useDesignerUIContext(context => context.state.selectedTab);
  const { t } = useTranslation('Host');
  const { isOpen } = useIsIntercomOpen();
  const { show, hide } = useIntercom();
  const isLargerThan500 = useBreakPoint(500);

  const isPassagesEnabled = useFlag('nativepassages');

  const dispatch = useDesignerDataContext(c => c.dispatch);
  const dispatchUi = useDesignerUIContext(c => c.dispatch);
  const isSlideListCollapsed = useDesignerUIContext(c => c.state.isSlideListCollapsed);

  const selectedSlide = useDesignerDataContext(selectedSlideSelector);
  const onSlideSelected = useDesignerDataContext(c => c.onSlideSelected);

  const onSlideDelete = useDesignerDataContext(c => c.handleSlideDelete);
  const onSlideCopy = useDesignerDataContext(c => c.handleSlideCopy);

  const handleSlidesDragged = useCallback(
    (slides: Slide[]) => {
      dispatch({ type: TemplateStateActions.slidesDragged, slides: slides });
    },
    [dispatch],
  );

  const handleNewSlideClicked = useDesignerDataContext(
    context => context.handleCreateNewSlide,
  );

  const slides = useDesignerDataContext(
    context => context.state.localTemplate?.content.slides || [],
  );

  const metrics = useMetrics();

  const totalNumberOfSlides = useMemo(() => {
    if (!slides) return undefined;
    return slides.filter(
      r => r.type !== 'voting' && r.type !== 'podium' && r.type !== 'responsegrid',
    ).length;
  }, [slides]);

  const selectedSlideNumber = useMemo(() => {
    if (!selectedSlide || !slides) return undefined;
    const filteredSlides = slides.filter(
      r => r.type !== 'voting' && r.type !== 'podium' && r.type !== 'responsegrid',
    );
    const index = filteredSlides.findIndex(slide => slide.id === selectedSlide.id);
    return index + 1;
  }, [slides, selectedSlide]);

  const handleHelpClicked = () => {
    if (isOpen) {
      hide();
    } else {
      show();
    }
  };

  const handleAttachmentsClicked = () => {
    dispatchUi({
      type: DesignerUIStateActions.setSelectedTab,
      payload: {
        tab: 'attachments',
      },
    });
  };
  const handleSlideZoomFit = useCallback(() => {
    const editingArea = editingAreaRef.current;
    if (!editingArea) return;
    const width = editingArea.clientWidth;
    const height = editingArea.clientHeight;
    metrics.logEvent('Creator.ZoomButton.Clicked', { zoom: 'Fit' });
    dispatchUi({
      type: DesignerUIStateActions.setZoom,
      containerFitHeight: height,
      containerFitWidth: width,
      zoomAction: 'fit',
      infiniteViewer: !!infiniteViewerRef.current ? infiniteViewerRef.current : undefined,
    });
  }, [dispatchUi, editingAreaRef, infiniteViewerRef, metrics]);

  // reset zoom when changing slides
  useEffect(() => {
    if (selectedSlide?.id) handleSlideZoomFit();
  }, [selectedSlide?.id]);

  const ZoomValueDropdownMenu = useMemo(
    () => (
      <Menu style={{ padding: '5px', maxHeight: 200, overflowY: 'auto' }}>
        <Menu.Item
          onClick={() => {
            handleSlideZoomFit();
          }}
        >
          Fit
        </Menu.Item>
        <Divider style={{ margin: '0' }} />

        <Menu.Item
          onClick={() => {
            metrics.logEvent('Creator.ZoomButton.Clicked', { zoom: 100 });
            dispatchUi({
              type: DesignerUIStateActions.setZoom,
              zoom: percentageToZoomValue(100),
            });
          }}
        >
          100%
        </Menu.Item>
        <Menu.Item
          onClick={() => {
            metrics.logEvent('Creator.ZoomButton.Clicked', { zoom: 75 });
            dispatchUi({
              type: DesignerUIStateActions.setZoom,
              zoom: percentageToZoomValue(75),
            });
          }}
        >
          75%
        </Menu.Item>
        <Menu.Item
          onClick={() => {
            metrics.logEvent('Creator.ZoomButton.Clicked', { zoom: 50 });
            dispatchUi({
              type: DesignerUIStateActions.setZoom,
              zoom: percentageToZoomValue(50),
            });
          }}
        >
          50%
        </Menu.Item>
        <Menu.Item
          onClick={() => {
            metrics.logEvent('Creator.ZoomButton.Clicked', { zoom: 25 });
            dispatchUi({
              type: DesignerUIStateActions.setZoom,
              zoom: percentageToZoomValue(25),
            });
          }}
        >
          25%
        </Menu.Item>
        <Menu.Item
          onClick={() => {
            metrics.logEvent('Creator.ZoomButton.Clicked', { zoom: 10 });
            dispatchUi({
              type: DesignerUIStateActions.setZoom,
              zoom: percentageToZoomValue(10),
            });
          }}
        >
          10%
        </Menu.Item>
      </Menu>
    ),
    [dispatchUi, editingAreaRef, infiniteViewerRef, metrics],
  );
  return (
    <FloatingSlideOverviewContainerWrapper>
      <CollapseSlideListButtonContainer>
        <CollapseSlideListButton
          onClick={() => {
            metrics.logEvent('Designer.SlideListCollapse');
            dispatchUi({
              type: DesignerUIStateActions.setSlideListCollapsed,
              isCollapsed: !isSlideListCollapsed,
            });
          }}
        >
          <Tooltip
            title={isSlideListCollapsed ? 'Show list of slides' : 'Hide list of slides'}
            mouseEnterDelay={1}
          >
            <CollapseButton $isCollapsed={isSlideListCollapsed}>
              <IconWrapper>
                <ArrowLeftSvg />
              </IconWrapper>
            </CollapseButton>
          </Tooltip>
        </CollapseSlideListButton>
      </CollapseSlideListButtonContainer>
      <FloatingSlideOverviewContainer>
        {/* Command/Ctrl plus hotkey */}
        <Hotkeys
          keyName="ctrl+=,command+=,ctrl+num_add,command+num_add"
          onKeyDown={(_, evnt) => {
            evnt.preventDefault();
            dispatchUi({
              type: DesignerUIStateActions.setZoom,
              zoomAction: 'increase',
            });
          }}
          allowRepeat={true}
        />
        {/* Command/Ctrl minus hotkey */}
        <Hotkeys
          keyName="ctrl+-,command+-,ctrl+num_remove,command+num_remove"
          onKeyDown={(_, evnt) => {
            evnt.preventDefault();
            dispatchUi({
              type: DesignerUIStateActions.setZoom,
              zoomAction: 'decrease',
            });
          }}
          allowRepeat={true}
        />

        {slides && (
          <>
            <DesignerSlideList
              slides={slides}
              selectedSlide={selectedSlide}
              onSlideSelected={onSlideSelected}
              onSlidesDragged={handleSlidesDragged}
              onSlideDelete={onSlideDelete}
              onSlideCopy={onSlideCopy}
              tabState={tabState}
              onNewSlideClicked={handleNewSlideClicked}
            />
            <BottomToolbarContainer>
              {isPassagesEnabled && (
                <Tooltip
                  title={
                    'Add reference materials that students can use throughout the lesson'
                  }
                  mouseEnterDelay={1}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    onClick={handleAttachmentsClicked}
                  >
                    <IconWrapper
                      onClick={handleHelpClicked}
                      style={{ flex: 0, marginRight: 5 }}
                      height="14px"
                      width="14px"
                      color="#5B5B5A"
                    >
                      <AttachmentSvg />
                    </IconWrapper>
                    <span>Materials</span>
                  </div>
                </Tooltip>
              )}
              <span
                style={{ flex: 1, fontSize: 14, userSelect: 'none', marginLeft: 10 }}
                onClick={() => {
                  dispatchUi({
                    type: DesignerUIStateActions.setSlideListCollapsed,
                    isCollapsed: false,
                  });
                }}
              >
                {selectedSlideNumber && totalNumberOfSlides && (
                  <>
                    {t('slidecounter', {
                      defaultValue: 'Slide {{selected}} of {{total}}',
                      selected: selectedSlideNumber,
                      total: totalNumberOfSlides,
                    })}
                  </>
                )}
              </span>
              <ZoomManualContainer>
                {isLargerThan500 && (
                  <div style={{ width: '200px' }}>
                    <Slider
                      min={1}
                      max={100}
                      style={{ width: '100%' }}
                      step={1}
                      trackStyle={{
                        backgroundColor: theme.colors.orange,
                      }}
                      tooltipVisible={false}
                      handleStyle={{
                        backgroundColor: theme.colors.orange,
                        borderColor: theme.colors.orange,
                      }}
                      value={zoomValueToPercentage(zoom)}
                      onChange={value => {
                        dispatchUi({
                          type: DesignerUIStateActions.setZoom,
                          zoom: percentageToZoomValue(value),
                        });
                      }}
                    />
                  </div>
                )}

                <Dropdown
                  overlay={ZoomValueDropdownMenu}
                  trigger={['click']}
                  placement="topCenter"
                >
                  <Tooltip title={'Zoom and fit to screen'}>
                    <ZoomManualButton type="ghost">
                      {Math.round(zoomValueToPercentage(zoom))}%
                    </ZoomManualButton>
                  </Tooltip>
                </Dropdown>
              </ZoomManualContainer>
              <Tooltip title={t('Help')} mouseEnterDelay={1}>
                <IconWrapper
                  onClick={handleHelpClicked}
                  style={{ flex: 0 }}
                  height="20px"
                  width="20px"
                  color="#5B5B5A"
                >
                  <QuestionMarkSvg />
                </IconWrapper>
              </Tooltip>
            </BottomToolbarContainer>
          </>
        )}
      </FloatingSlideOverviewContainer>
    </FloatingSlideOverviewContainerWrapper>
  );
};

const BottomToolbarContainer = styled.div`
  display: flex;
  align-items: center;
  z-index: ${zIndexes('DESIGNER_SLIDE_LIST')};
  gap: 12px;
  padding: 0px 10px;
  height: 40px;
`;

const FloatingSlideOverviewContainerWrapper = styled.div`
  width: 100%;
  height: 100%;
  background-color: transparent;
  position: relative;
`;
const CollapseSlideListButton = styled.div`
  border-radius: 10px;
  padding: 0;

  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  background-color: ${theme.colors.white};
  z-index: 100;
  cursor: pointer;
`;

const CollapseSlideListButtonContainer = styled.div`
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  z-index: ${zIndexes('DESIGNER_SLIDE_LIST_HIDE')};
`;
const FloatingSlideOverviewContainer = styled.div`
  width: calc(100% - 5px);
  height: 100%;
  position: relative;
  background-color: white;
  display: flex;
  margin-left: 1px;
  margin-right: 5px;
  flex-direction: column;
  z-index: ${zIndexes('DESIGNER_SLIDE_LIST')};
  border-radius: ${theme.effects.floatingCreatorElementBorderRadius};
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  overflow: hidden;
`;

const CollapseButton = styled.div<{ $isCollapsed: boolean }>`
  background-color: white;
  height: 15px;
  width: 100px;
  display: flex;
  justify-content: center;
  padding: 5px;
  transform: translateY(-100%);
  border-radius: 10px 10px 0 0;
  animation: 0.3s ease-in-out;
  svg {
    margin: 0;
    width: 20px !important;
    height: 20px !important;

    ${({ $isCollapsed }) =>
      $isCollapsed ? 'transform: rotate(90deg);' : 'transform: rotate(-90deg)'}
  }
`;

const ZoomManualContainer = styled.div`
  padding: 0 5px;
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;

  border-radius: 5px;
  background-color: ${theme.colors.white};
  .ant-slider-handle:focus {
    box-shadow: 0 0 0 2px ${theme.colors.translucentOrange};
  }
`;
const ZoomManualButton = styled(Button)`
  padding: 2px;
  border: none;
  width: 50px;
  margin-left: 10px;
`;

export default memo(FloatingSlideOverviewXY);
